import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const CLIENT_ID = 'emoai18ohrwuefxlhdanyszvl8mr2f';
const REDIRECT_URI = 'https://poll.redjulu.de';
const BACKEND_URL = '/api';

const DEFAULT_DESIGN = {
  shape: 'circle',
  roundness: 27,
  accentColor: '#a855f7',
  bgColor: 'rgba(0,0,0,0.85)',
  textAlign: 'left',
  padding: 22,
  glowIntensity: 40,
  width: 261,
  minHeight: 150,
  scale: 1.0,
  fontSize: 14,
  // Balken & Details
  barColor: 'rgba(255,255,255,0.1)',
  barHeight: 8,
  barRoundness: 20,
  barSpacing: 14,
  showPercentage: true,
  textColor: '#ffffff',
  fontWeight: 900,
  shadowIntensity: 20,
  fullSource: true // Instruktion für Zentrierung
};

function App() {
  const [token, setToken] = useState(localStorage.getItem('twitch_token'));
  const [user, setUser] = useState(null);
  const [modChannels, setModChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [isChannelDropdownOpen, setIsChannelDropdownOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [showEndModal, setShowEndModal] = useState(false);
  const [isPollRunning, setIsPollRunning] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [showInfo, setShowInfo] = useState(false);
  const [openSection, setOpenSection] = useState('geo');
  const [design, setDesign] = useState(JSON.parse(localStorage.getItem('poll_design')) || DEFAULT_DESIGN);

  const dropdownRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem('twitch_token');
    setToken(null);
    setUser(null);
    setModChannels([]);
    setActiveChannel(null);
    window.location.hash = "";
  };

  useEffect(() => {
    localStorage.setItem('poll_design', JSON.stringify(design));
  }, [design]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (r) => r,
      (e) => {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          handleLogout();
        }
        return Promise.reject(e);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      const _token = params.get('access_token');
      if (_token) {
        localStorage.setItem('twitch_token', _token);
        setToken(_token);
        window.history.replaceState({}, document.title, "/");
      }
    }
  }, []);

  useEffect(() => {
    if (token && !user) {
      const timer = setTimeout(() => { fetchUser(); }, 150);
      return () => clearTimeout(timer);
    }
  }, [token, user]);

  useEffect(() => {
    if (activeChannel && token) {
      const checkActivePoll = async () => {
        try {
          const res = await axios.get(`${BACKEND_URL}/status?channel=${activeChannel.login}`);
          if (res.data && res.data.active) {
            setIsPollRunning(true);
            setQuestion(res.data.question);
            setOptions(res.data.options);
          } else {
            setIsPollRunning(false);
          }
        } catch (e) { console.error("Poll status check failed", e); }
      };
      checkActivePoll();
      const interval = setInterval(checkActivePoll, 5000);
      return () => clearInterval(interval);
    }
  }, [activeChannel, token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsChannelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUser = async () => {
    if (!token) return;
    try {
      const res = await axios.get('https://api.twitch.tv/helix/users', {
        headers: { Authorization: `Bearer ${token}`, 'Client-Id': CLIENT_ID }
      });
      if (res.data.data && res.data.data.length > 0) {
        const userData = res.data.data[0];
        setUser(userData);
        setActiveChannel(userData);
        fetchModChannels(userData.id, token);
      }
    } catch (err) { handleLogout(); }
  };

  const fetchModChannels = async (userId, currentToken) => {
    try {
      const res = await axios.get(`https://api.twitch.tv/helix/moderation/channels?user_id=${userId}`, {
        headers: { Authorization: `Bearer ${currentToken}`, 'Client-Id': CLIENT_ID }
      });
      setModChannels(res.data.data || []);
    } catch (e) { console.error("Failed to fetch mod channels", e); }
  };

  const addToast = (msg) => {
    const id = Date.now();
    setToasts((p) => [...p, { id, msg, exiting: false }]);
    setTimeout(() => {
      setToasts((p) => p.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
      setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 400);
    }, 3000);
  };

  const startPoll = async () => {
    const validOptions = options.filter((o) => o.trim() !== '');
    if (!question) return addToast('Fehler: Frage fehlt!');
    if (validOptions.length < 2) return addToast('Fehler: Zu wenige Optionen!');
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/start`, {
        question, options: validOptions, channel: activeChannel.login, design
      });
      setIsPollRunning(true);
      addToast(`Live auf ${activeChannel.display_name}!`);
    } catch { addToast('Start fehlgeschlagen.'); }
    setLoading(false);
  };

  const endPoll = async (showResults) => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/end`, { channel: activeChannel.login, showResults });
      setIsPollRunning(false);
      setShowEndModal(false);
      setQuestion('');
      setOptions(['', '']);
      addToast(showResults ? 'Mit Ergebnissen beendet' : 'Beendet');
    } catch { addToast('Fehler beim Beenden.'); }
    setLoading(false);
  };

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    setTilt({ x: (x / rect.width) * 20, y: (y / rect.height) * -20 });
  };

  const loginWithTwitch = () => {
    const scopes = encodeURIComponent('user:read:email user:read:moderated_channels');
    window.location.href = `https://id.twitch.tv/oauth2/authorize?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=token&scope=${scopes}`;
  };

  const getCompressedDesign = () => {
    const diff = {};
    Object.keys(design).forEach(key => {
      if (design[key] !== DEFAULT_DESIGN[key]) diff[key] = design[key];
    });
    return Object.keys(diff).length > 0 ? encodeURIComponent(JSON.stringify(diff)) : '';
  };

  const obsUrl = activeChannel ? `https://poll.redjulu.de/overlay.html?channel=${activeChannel.login}${getCompressedDesign() ? `&d=${getCompressedDesign()}` : ''}` : '';

  if (!token) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-6 perspective-tight">
        <div className="animate-card-in">
          <div
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setTilt({ x: 0, y: 0 })}
            style={{
              transform: `rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`,
              transition: tilt.x === 0 ? 'transform 0.6s ease' : 'transform 0.1s ease',
              willChange: 'transform'
            }}
            className="glass glint p-12 rounded-[3.5rem] text-center max-w-sm w-full shadow-[0_50px_100px_rgba(0,0,0,0.5)] preserve-3d"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-purple-600 to-fuchsia-500 rounded-[2.2rem] mx-auto mb-8 flex items-center justify-center shadow-2xl translate-z-60">
              <svg className="w-12 h-12 fill-white" viewBox="0 0 24 24">
                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
              </svg>
            </div>
            <h1 className="text-4xl font-black mb-12 italic uppercase bg-gradient-to-b from-white to-zinc-500 bg-clip-text text-transparent translate-z-60">RedJulu Poll</h1>
            <button
              onClick={loginWithTwitch}
              className="w-full bg-white text-black hover:bg-purple-600 hover:text-white py-5 rounded-2xl font-black uppercase tracking-widest transition-all shadow-2xl translate-z-60 cursor-pointer"
            >Twitch Login</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center p-6">
      <div className="fixed bottom-6 right-6 z-[100000] flex flex-col gap-3 w-80 pointer-events-none">
        {toasts.map((t) => (
          <div key={t.id} className={`glass rounded-2xl shadow-2xl transition-all duration-400 overflow-hidden border-white/5 pointer-events-auto ${t.exiting ? 'opacity-0 scale-95' : 'animate-toast-in opacity-100'}`}>
            <div className="p-4 flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />
              <p className="text-[10px] font-black uppercase tracking-widest text-white">{t.msg}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="w-full max-w-2xl animate-card-in">
        <div className="h-24 flex items-center justify-between glass p-5 rounded-[2rem] mb-4 shadow-xl border-white/5 relative !overflow-visible z-[5000]">
          <div className="flex items-center gap-4 h-full !overflow-visible">
            <img src={user?.profile_image_url} className="w-10 h-10 rounded-xl border border-purple-600/30" alt="pfp" />
            <div className="relative h-full flex items-center !overflow-visible" ref={dropdownRef}>
              <button onClick={() => setIsChannelDropdownOpen(!isChannelDropdownOpen)} className="flex items-center gap-2 px-4 py-2 hover:bg-white/5 rounded-2xl transition-all cursor-pointer group">
                <p className="font-black text-lg tracking-tighter uppercase italic">{activeChannel?.display_name || '...'}</p>
                <svg className={`w-4 h-4 text-purple-500 transition-transform ${isChannelDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path d="M19 9l-7 7-7-7"/></svg>
              </button>
              {isChannelDropdownOpen && (
                <div className="absolute top-[calc(100%+10px)] left-0 min-w-[320px] bg-[#0c0c0c] border border-white/10 rounded-[2.5rem] shadow-[0_25px_100px_rgba(0,0,0,1)] z-[99999] overflow-hidden backdrop-blur-3xl animate-in fade-in slide-in-from-top-4 duration-200">
                  <div className="p-3 space-y-2">
                    <button onClick={() => { setActiveChannel(user); setIsChannelDropdownOpen(false); }} className={`w-full text-left px-8 py-5 rounded-[2rem] text-[12px] font-black uppercase tracking-widest transition-all ${activeChannel?.id === user?.id ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/40' : 'text-zinc-500 hover:bg-white/5'}`}>Mein Kanal</button>
                    <div className="h-[1px] bg-white/5 mx-8 my-1" />
                    <p className="px-8 py-2 text-[9px] font-black text-zinc-600 uppercase tracking-[0.4em]">Moderiert</p>
                    <div className="max-h-[300px] overflow-y-auto custom-scrollbar px-1 pb-2">
                      {modChannels.map((ch) => (
                        <button key={ch.broadcaster_id} onClick={() => { setActiveChannel({ id: ch.broadcaster_id, login: ch.broadcaster_login, display_name: ch.broadcaster_name }); setIsChannelDropdownOpen(false); }} className={`w-full text-left px-8 py-5 rounded-[2rem] text-[12px] font-black uppercase tracking-widest transition-all ${activeChannel?.id === ch.broadcaster_id ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/40' : 'text-zinc-500 hover:bg-white/5'}`}>{ch.broadcaster_name}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowInfo(true)} className="p-2 text-purple-400 hover:text-white transition-all group cursor-pointer">
              <svg className="w-6 h-6 group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/></svg>
            </button>
            <button onClick={handleLogout} className="p-2 text-zinc-600 hover:text-red-500 transition-all cursor-pointer">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            </button>
          </div>
        </div>

        <div className="glass glint rounded-[3.5rem] shadow-2xl w-full relative z-0">
          <div className="p-10">
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-[14px] font-black uppercase tracking-[0.3em] text-purple-500/80 ml-1">Fragestellung</label>
                <input disabled={isPollRunning} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Worüber stimmen wir ab?" className="w-full bg-black/40 border border-white/5 rounded-[1.5rem] px-8 py-5 focus:border-purple-600 outline-none transition-all font-bold text-xl placeholder:text-zinc-700" />
              </div>
              <div className="space-y-3">
                <label className="text-[14px] font-black uppercase tracking-[0.3em] text-purple-500/80 ml-1">Optionen</label>
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                  {options.map((opt, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="relative flex-1">
                        <span className="absolute left-6 top-1/2 -translate-y-1/2 text-purple-600/40 font-black italic">0{i + 1}</span>
                        <input disabled={isPollRunning} value={opt} onChange={(e) => { const n = [...options]; n[i] = e.target.value; setOptions(n); }} className="w-full bg-black/40 border border-white/5 rounded-2xl pl-14 pr-6 py-4 focus:border-purple-500 outline-none transition-all font-medium" placeholder={`Option ${i + 1}`} />
                      </div>
                      {options.length > 2 && !isPollRunning && (
                        <button onClick={() => setOptions(options.filter((_, idx) => idx !== i))} className="px-4 bg-red-950/20 hover:bg-red-600 text-red-600 hover:text-white rounded-2xl transition-all border border-red-500/10">✕</button>
                      )}
                    </div>
                  ))}
                </div>
                {!isPollRunning && (
                  <button onClick={() => setOptions([...options, ''])} className="w-full py-4 mt-2 bg-white/[0.02] border border-dashed border-white/10 rounded-2xl text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-purple-400 transition-all">+ Option hinzufügen</button>
                )}
              </div>
            </div>
          </div>
          <div className="bg-black/40 p-8 flex gap-4 border-t border-white/5 flex-wrap">
            <button onClick={startPoll} disabled={loading || isPollRunning} className={`flex-[2] py-5 rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl transition-all ${isPollRunning ? 'bg-zinc-800 text-zinc-600' : 'bg-purple-600 hover:bg-purple-500 active:scale-95'}`}>{isPollRunning ? 'Poll aktiv' : 'Launch Poll'}</button>
            <button onClick={() => isPollRunning ? setShowEndModal(true) : addToast('Keine aktive Umfrage!')} className="flex-1 bg-zinc-900 hover:bg-red-900/40 py-5 rounded-2xl font-black uppercase text-[10px] tracking-widest border border-white/5">Beenden</button>
          </div>
        </div>
      </div>

      {showInfo && (
        <div className="fixed inset-0 z-[20000] flex items-center justify-center p-6 bg-zinc-950/80 backdrop-blur-[50px]" onClick={() => setShowInfo(false)}>
          <div className="glass p-10 rounded-[4rem] max-w-5xl w-full border-white/5 flex flex-col gap-8 animate-card-in overflow-hidden max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-4">
              <div className="flex items-center gap-4">
                <h3 className="text-3xl font-black italic uppercase">Designer</h3>
                <button onClick={() => setDesign(DEFAULT_DESIGN)} className="px-4 py-1.5 bg-white/5 hover:bg-white/10 rounded-full text-[8px] font-black uppercase tracking-widest text-zinc-400 transition-all">Reset Default</button>
              </div>
              <button onClick={() => setShowInfo(false)} className="p-3 bg-white/5 hover:bg-red-500/20 text-zinc-500 hover:text-red-500 rounded-2xl transition-all"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"/></svg></button>
            </div>

            <div className="flex gap-10 overflow-hidden flex-1 px-4">
              <div className="w-[380px] flex flex-col bg-zinc-950/50 rounded-[3rem] border border-white/5 p-8 relative items-center justify-center overflow-hidden">
                <p className="absolute top-6 text-[9px] font-black text-zinc-600 uppercase tracking-[0.4em]">Live Preview</p>
                <div style={{ transform: `scale(${design.scale * 0.85})` }}>
                  <div style={{ 
                    backgroundColor: design.bgColor, 
                    borderRadius: design.shape === 'circle' ? `${design.roundness}px` : '4px', 
                    padding: `${design.padding}px`, 
                    textAlign: design.textAlign, 
                    boxShadow: `inset 0 0 ${design.glowIntensity}px ${design.accentColor}44, 0 ${design.shadowIntensity / 2}px ${design.shadowIntensity}px rgba(0,0,0,0.5)`, 
                    border: `1px solid ${design.accentColor}33`, 
                    width: `${design.width}px`, 
                    minHeight: `${design.minHeight}px`,
                    color: design.textColor
                  }}>
                    <h4 className="italic uppercase mb-4" style={{ fontSize: `${design.fontSize}px`, fontWeight: design.fontWeight }}>{question || "Vorschau Frage"}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: `${design.barSpacing}px` }}>
                        {(options.length > 0 ? options : ['', '']).map((opt, i) => (
                            <div key={i} className="flex flex-col gap-1.5">
                                <div className="flex justify-between text-[8px] uppercase font-black opacity-80" style={{ fontSize: `${design.fontSize * 0.6}px` }}>
                                    <span>{opt || `Option ${i+1}`}</span>
                                    {design.showPercentage && <span>{30 + (i*15)}%</span>}
                                </div>
                                <div style={{ 
                                    height: `${design.barHeight}px`, 
                                    backgroundColor: design.barColor, 
                                    borderRadius: `${design.barRoundness}px`,
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}>
                                    <div style={{ 
                                        height: '100%', 
                                        width: `${30 + (i*15)}%`, 
                                        backgroundColor: design.accentColor,
                                        boxShadow: `0 0 10px ${design.accentColor}88`
                                    }} />
                                </div>
                            </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 space-y-4 overflow-y-auto pr-4 custom-scrollbar pb-10">
                {[
                  { id: 'geo', icon: '📐', title: 'Container & Quelle', content: (
                    <div className="p-6 space-y-6 bg-black/40 rounded-[1.5rem] border border-white/5">
                      <div className="flex items-center justify-between p-4 bg-purple-600/20 rounded-xl border border-purple-500/30">
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black uppercase tracking-widest text-white">Quelle füllen</span>
                            <span className="text-[8px] text-purple-300/60 uppercase">Zentriert das Poll in der OBS Quelle</span>
                        </div>
                        <input type="checkbox" checked={design.fullSource} onChange={e => setDesign({...design, fullSource: e.target.checked})} className="w-5 h-5 accent-purple-500" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        {['circle', 'square'].map(s => <button key={s} onClick={() => setDesign({...design, shape: s})} className={`py-4 rounded-xl border text-[9px] font-black uppercase tracking-widest ${design.shape === s ? 'bg-purple-600 border-purple-400' : 'bg-black border-white/10 text-zinc-600'}`}>{s === 'circle' ? 'Abgerundet' : 'Eckig'}</button>)}
                      </div>
                      <DesignSlider label="Breite" val={design.width} min={180} max={1200} unit="px" onChange={v => setDesign({...design, width: v})} />
                      <DesignSlider label="Min. Höhe" val={design.minHeight} min={50} max={1000} unit="px" onChange={v => setDesign({...design, minHeight: v})} />
                      <DesignSlider label="Ecken-Radius" val={design.roundness} min={0} max={150} unit="px" onChange={v => setDesign({...design, roundness: v})} />
                      <DesignSlider label="Innenabstand" val={design.padding} min={5} max={120} unit="px" onChange={v => setDesign({...design, padding: v})} />
                      <DesignSlider label="Zoom / Scale" val={design.scale} min={0.3} max={4.0} step={0.1} unit="x" onChange={v => setDesign({...design, scale: v})} />
                    </div>
                  )},
                  { id: 'bars', icon: '📊', title: 'Balken Design', content: (
                    <div className="p-6 space-y-6 bg-black/40 rounded-[1.5rem] border border-white/5">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-400 uppercase">Balken Hintergrund</label>
                        <input type="color" value="#333333" onChange={e => {
                            const r = parseInt(e.target.value.slice(1,3), 16), g = parseInt(e.target.value.slice(3,5), 16), b = parseInt(e.target.value.slice(5,7), 16);
                            setDesign({...design, barColor: `rgba(${r},${g},${b},0.3)`});
                        }} className="w-full h-8 bg-transparent cursor-pointer" />
                      </div>
                      <DesignSlider label="Balken Höhe" val={design.barHeight} min={2} max={100} unit="px" onChange={v => setDesign({...design, barHeight: v})} />
                      <DesignSlider label="Balken Rundung" val={design.barRoundness} min={0} max={50} unit="px" onChange={v => setDesign({...design, barRoundness: v})} />
                      <DesignSlider label="Abstand zw. Optionen" val={design.barSpacing} min={4} max={100} unit="px" onChange={v => setDesign({...design, barSpacing: v})} />
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                        <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Prozent anzeigen</span>
                        <input type="checkbox" checked={design.showPercentage} onChange={e => setDesign({...design, showPercentage: e.target.checked})} className="w-5 h-5 accent-purple-500" />
                      </div>
                    </div>
                  )},
                  { id: 'style', icon: '🎨', title: 'Farben & Effekte', content: (
                    <div className="p-6 space-y-6 bg-black/40 rounded-[1.5rem] border border-white/5">
                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-zinc-400 uppercase">Akzent Farbe</label>
                          <input type="color" value={design.accentColor} onChange={e => setDesign({...design, accentColor: e.target.value})} className="w-full h-10 bg-transparent rounded-lg cursor-pointer" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-zinc-400 uppercase">Text Farbe</label>
                          <input type="color" value={design.textColor} onChange={e => setDesign({...design, textColor: e.target.value})} className="w-full h-10 bg-transparent rounded-lg cursor-pointer" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-zinc-400 uppercase">Overlay Hintergrund</label>
                        <input type="color" value="#000000" onChange={e => {
                          const r = parseInt(e.target.value.slice(1,3), 16), g = parseInt(e.target.value.slice(3,5), 16), b = parseInt(e.target.value.slice(5,7), 16);
                          setDesign({...design, bgColor: `rgba(${r},${g},${b},0.85)`});
                        }} className="w-full h-10 bg-transparent rounded-lg cursor-pointer" />
                      </div>
                      <DesignSlider label="Glow (Innen)" val={design.glowIntensity} min={0} max={200} unit="px" onChange={v => setDesign({...design, glowIntensity: v})} />
                      <DesignSlider label="Schatten (Außen)" val={design.shadowIntensity} min={0} max={100} unit="px" onChange={v => setDesign({...design, shadowIntensity: v})} />
                    </div>
                  )},
                  { id: 'typo', icon: '✍️', title: 'Typografie', content: (
                    <div className="p-6 space-y-6 bg-black/40 rounded-[1.5rem] border border-white/5">
                      <div className="grid grid-cols-3 gap-3">
                        {['left', 'center', 'right'].map(a => <button key={a} onClick={() => setDesign({...design, textAlign: a})} className={`py-3 rounded-xl border text-[9px] font-black uppercase ${design.textAlign === a ? 'bg-purple-600' : 'bg-black text-zinc-600'}`}>{a}</button>)}
                      </div>
                      <DesignSlider label="Schriftgröße" val={design.fontSize} min={8} max={80} unit="px" onChange={v => setDesign({...design, fontSize: v})} />
                      <DesignSlider label="Schriftstärke" val={design.fontWeight} min={100} max={900} step={100} unit="" onChange={v => setDesign({...design, fontWeight: v})} />
                    </div>
                  )}
                ].map(sec => (
                  <div key={sec.id} className="space-y-2">
                    <button onClick={() => setOpenSection(openSection === sec.id ? '' : sec.id)} className="w-full flex justify-between p-5 bg-white/5 rounded-[1.5rem] hover:bg-white/10 text-[10px] font-black uppercase tracking-widest border border-white/5">
                      <span>{sec.icon} {sec.title}</span>
                      <span className="text-purple-500 text-xl">{openSection === sec.id ? '−' : '+'}</span>
                    </button>
                    {openSection === sec.id && sec.content}
                  </div>
                ))}

                <div className="bg-purple-600/10 p-8 rounded-[2.5rem] border border-purple-500/20 mt-6 overflow-hidden">
                  <p className="text-[9px] font-black text-purple-400 uppercase mb-4 tracking-[0.3em]">OBS Overlay Link</p>
                  <div className="bg-black/40 p-5 rounded-2xl border border-white/5 mb-5"><code className="text-[10px] text-zinc-300 break-all block font-mono leading-relaxed">{obsUrl}</code></div>
                  <button onClick={() => {navigator.clipboard.writeText(obsUrl); addToast('Kopiert!');}} className="w-full py-5 bg-purple-600 hover:bg-purple-500 text-white text-[11px] font-black uppercase tracking-widest rounded-2xl transition-all shadow-xl">URL kopieren</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEndModal && (
        <div className="fixed inset-0 z-[30000] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md" onClick={() => setShowEndModal(false)}>
          <div className="glass p-12 rounded-[3rem] max-w-md w-full text-center border-purple-500/30 animate-card-in" onClick={e => e.stopPropagation()}>
            <h3 className="text-2xl font-black mb-4 italic uppercase text-purple-400">Beenden?</h3>
            <div className="flex flex-col gap-4 mt-8">
              <button onClick={() => endPoll(true)} className="bg-purple-600 py-6 rounded-2xl font-black uppercase text-xs tracking-widest shadow-2xl transition-all">Mit Ergebnissen</button>
              <button onClick={() => endPoll(false)} className="bg-zinc-800 py-6 rounded-2xl font-black uppercase text-xs tracking-widest border border-white/5">Verwerfen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DesignSlider({ label, val, min, max, unit, step = 1, onChange }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-end"><label className="text-[9px] font-black uppercase text-zinc-400 tracking-widest">{label}</label><span className="text-xs font-mono text-purple-400">{val}{unit}</span></div>
      <input type="range" min={min} max={max} step={step} value={val} onChange={e => onChange(parseFloat(e.target.value))} className="w-full accent-purple-500 h-1.5 bg-white/5 rounded-full appearance-none cursor-pointer" />
    </div>
  );
}

export default App;